import { IChartData } from "@/interfaces/dashboard.interface";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Skeleton } from "../ui/skeleton";

interface LineChartCardProps {
  title: string;
  data: IChartData[];
  percentage: number;
  period: string;
  isLoading?: boolean;
}

export default function LineChartCard({ title, data, percentage, period, isLoading }: LineChartCardProps) {
  const isPositive = percentage >= 0;

  return (
    <div className="bg-darkblue rounded-[20px] p-[30px] w-full lg:w-[428px] h-[209px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-[#F6F6F6]">{title}</h3>
          <div className="bg-[#0C1427] rounded-[20px] px-[10px] py-1">
            <p className={`text-xs ${isPositive ? 'text-[#13D480]' : 'text-[#DC4426]'}`}>
              {isPositive ? '+' : ''}{percentage}%
            </p>
          </div>
        </div>
        <p className="text-xs text-[#888888]">{period}</p>
      </div>

      {/* Value */}
      <p className="text-xl font-bold text-white mb-4">
        R${data.reduce((sum, item) => sum + item.value1, 0).toLocaleString('pt-BR')}
      </p>

      {/* Chart */}
      <div className="flex-1 min-h-[100px]">
        {isLoading ? (
          <Skeleton className="h-full" />
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Sem dados disponíveis
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={100}>
            <LineChart data={data}>
              <defs>
                <linearGradient id="colorValue1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7846FF" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#7846FF" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorValue2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#45169C" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#45169C" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis hide />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#15203C",
                  border: "1px solid #5D5D5D",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F6F6F6" }}
                itemStyle={{ color: "#F6F6F6" }}
              />
              <Line
                type="monotone"
                dataKey="value1"
                stroke="#7846FF"
                strokeWidth={2}
                dot={false}
                fill="url(#colorValue1)"
              />
              <Line
                type="monotone"
                dataKey="value2"
                stroke="#45169C"
                strokeWidth={2}
                dot={false}
                fill="url(#colorValue2)"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mt-3">
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#7846FF]" />
          <p className="text-xs text-white">Completos</p>
        </div>
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#45169C]" />
          <p className="text-xs text-white">Ativos</p>
        </div>
      </div>
    </div>
  );
}
